import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIdentity } from '../../hooks/useIdentity';
import useOnline from '../../hooks/useOnline';
import { useSystemBanner } from '../../hooks/useSystemBanner';
import useToast from '../../components/toast/useToast';
import { subscribeMessages, sendMessage } from './firestore';
import { subscribeTyping } from './typing';
import MessageList from './MessageList';
import Composer from './Composer';
import TypingIndicator from './TypingIndicator';
import EmptyParty from './EmptyParty';
import NewMessagesPill from './NewMessagesPill';
import BannerHost from './BannerHost';
import DisconnectedBanner from './DisconnectedBanner';
import { recordRoomActivity } from '../nav/roomHistoryStore';
import './ChatRegion.css';

// "Near the bottom" tolerance — within this many pixels of the bottom of the
// scroll container we still auto-scroll on receive. Anything beyond and we
// switch into "scrolled away" mode (show the NewMessagesPill instead).
const NEAR_BOTTOM_PX = 64;

// Visible-window pagination knobs. Initial render shows the newest
// INITIAL_PAGE_SIZE confirmed messages; scrolling within NEAR_TOP_PX of
// the top expands the window by PAGE_SIZE up to messages.length. The full
// confirmed list is held in memory (capped in firestore.js); this is
// purely a render-time slice, no extra Firestore reads.
const INITIAL_PAGE_SIZE = 20;
const PAGE_SIZE = 20;
const NEAR_TOP_PX = 120;

// How long an optimistic message may stay "pending" before we flip it to
// "failed". 10s is long enough for slow networks but short enough to surface
// a retry affordance before the user gives up.
const OPTIMISTIC_TIMEOUT_MS = 10_000;

const GLOB = 'GLOB';

// Inline orchestration hook: when IdentityProvider's expired-party probe
// surfaces an `expiredFrom` code, fire a single info banner then clear it.
// Defined here (instead of as a separate file) because it's a one-shot glue
// component with no reuse value outside ChatRegion.
function ExpiredPartyBanner() {
  const { expiredFrom, clearExpired } = useIdentity();
  const { showBanner } = useSystemBanner();
  useEffect(() => {
    if (!expiredFrom) return;
    showBanner({
      kind: 'info',
      text: `Party ${expiredFrom} expired — back in #GLOB`,
    });
    clearExpired();
  }, [expiredFrom, clearExpired, showBanner]);
  return null;
}

/**
 * ChatRegion — owner of the chat surface.
 *
 * Responsibilities (kept narrow on purpose):
 *   - Subscribe to Firestore via subscribeMessages and return the unsubscribe
 *     from useEffect so partyCode changes / unmount tear down cleanly. This
 *     is the fix for the legacy listener leak.
 *   - Subscribe to typing presence for the same partyCode; filter own user
 *     and feed the indicator.
 *   - Maintain an optimistic-message Map keyed by localId so the user sees
 *     their message instantly; replace with the confirmed Firestore doc on
 *     match (by senderId + text + recent sentAt — best-effort).
 *   - Track whether the user is scrolled away and accumulate unread count;
 *     auto-scroll only on own send or when already near the bottom.
 *   - Mount the system-banner host, disconnected banner, and the expired-party
 *     glue hook so any feature can surface its message inside the chat region.
 */
function ChatRegion() {
  const { user, handle, partyCode, avatarSeed } = useIdentity();
  const online = useOnline();
  const { showToast } = useToast();

  const [messages, setMessages] = useState([]);
  // Map<localId, { id, text, senderId, senderName, avatarSeed, sentAt, status }>
  const [optimisticMessages, setOptimisticMessages] = useState(() => new Map());
  const [unreadCount, setUnreadCount] = useState(0);
  const [scrolledAway, setScrolledAway] = useState(false);
  const [typers, setTypers] = useState([]);
  // Visible-window pagination — number of confirmed messages rendered from
  // the tail of `messages`. Starts at INITIAL_PAGE_SIZE; scroll-up bumps it
  // by PAGE_SIZE up to messages.length.
  const [visibleCount, setVisibleCount] = useState(INITIAL_PAGE_SIZE);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const scrollRef = useRef(null);
  const anchorRef = useRef(null);
  // Tracks whether the next scroll should be forced (own-send or pill click).
  const forceScrollRef = useRef(false);
  // Tracks the previous Firestore message count to detect "received message".
  const prevMsgCountRef = useRef(0);
  // Latest scrolledAway value for use inside the snapshot callback.
  const scrolledAwayRef = useRef(false);
  useEffect(() => {
    scrolledAwayRef.current = scrolledAway;
  }, [scrolledAway]);
  // Per-message timeout ids so unmount / dedupe can clear them.
  const failTimersRef = useRef(new Map());
  // Reconnect toast: track previous online state to fire on the transition.
  const wasOnlineRef = useRef(online);
  // Scroll-anchor bookkeeping for the load-older flow: capture the scroll
  // container's height BEFORE prepending older messages so the useLayoutEffect
  // below can restore the visual position (otherwise the user gets yanked to
  // the top every time more loads).
  const anchorScrollHeightRef = useRef(null);

  const selfId = user || handle || '';

  // Connected toast: only on a false → true transition. Skip the first paint.
  useEffect(() => {
    if (wasOnlineRef.current === online) return;
    if (!wasOnlineRef.current && online) {
      showToast('Connected');
    }
    wasOnlineRef.current = online;
  }, [online, showToast]);

  // Cleanup pending fail timers on unmount.
  useEffect(
    () => () => {
      for (const t of failTimersRef.current.values()) clearTimeout(t);
      failTimersRef.current.clear();
    },
    [],
  );

  // ── Firestore subscription ──────────────────────────────────────────────
  //
  // subscribeMessages returns the full sorted message list for the room
  // (capped in the data layer). The render-time slice below decides how
  // many we actually paint.
  useEffect(() => {
    if (!partyCode) return undefined;
    const unsubscribe = subscribeMessages(partyCode, (rows) => {
      setMessages(rows);
      // Drop any optimistic entry that now has a confirmed match (same sender
      // + same text). We match against the freshly-received rows so dedupe
      // happens in a single pass. Also clear the per-optimistic fail timer.
      setOptimisticMessages((prev) => {
        if (prev.size === 0) return prev;
        const next = new Map(prev);
        let mutated = false;
        for (const [localId, opt] of prev) {
          const confirmed = rows.find(
            (r) => r.senderId === opt.senderId && r.text === opt.text,
          );
          if (confirmed) {
            next.delete(localId);
            const timer = failTimersRef.current.get(localId);
            if (timer) {
              clearTimeout(timer);
              failTimersRef.current.delete(localId);
            }
            mutated = true;
          }
        }
        return mutated ? next : prev;
      });
    });
    // Critical: return the unsubscribe so the listener tears down on
    // partyCode change and on unmount.
    return unsubscribe;
  }, [partyCode]);

  // ── Load older — expand the visible window on scroll-up ─────────────────
  //
  // No extra Firestore reads — the full list is already in memory. We just
  // unhide PAGE_SIZE more rows from the tail. A one-frame delay keeps the
  // "Loading older…" indicator from popping in and out imperceptibly, which
  // made the affordance feel broken in testing.
  const loadOlder = useCallback(() => {
    if (loadingOlder) return;
    if (visibleCount >= messages.length) return; // nothing older to reveal
    setLoadingOlder(true);
    // Capture height now so the useLayoutEffect can restore the user's
    // visual scroll position after the prepend.
    anchorScrollHeightRef.current = scrollRef.current?.scrollHeight ?? null;
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (fn) => setTimeout(fn, 0);
    raf(() => {
      setVisibleCount((c) => Math.min(c + PAGE_SIZE, messages.length));
      setLoadingOlder(false);
    });
  }, [loadingOlder, visibleCount, messages.length]);

  // Restore scroll position after the visible window grows. Without this,
  // the browser keeps `scrollTop` constant but the content above grows,
  // yanking the user to the top.
  useLayoutEffect(() => {
    const prevHeight = anchorScrollHeightRef.current;
    if (prevHeight == null) return;
    const node = scrollRef.current;
    if (!node) return;
    const delta = node.scrollHeight - prevHeight;
    if (delta > 0) node.scrollTop = node.scrollTop + delta;
    anchorScrollHeightRef.current = null;
  }, [visibleCount, messages]);

  // ── Typing subscription — feeds the TypingIndicator ──────────────────────
  useEffect(() => {
    if (!partyCode) return undefined;
    const unsubscribe = subscribeTyping(partyCode, (rows) => {
      setTypers(rows.filter((r) => r.id !== selfId));
    });
    return unsubscribe;
  }, [partyCode, selfId]);

  // Reset unread + optimistic + pagination state when partyCode changes —
  // the new room has its own scroll position and pending messages don't
  // carry over.
  useEffect(() => {
    setMessages([]);
    setUnreadCount(0);
    setScrolledAway(false);
    setOptimisticMessages(new Map());
    setVisibleCount(INITIAL_PAGE_SIZE);
    setLoadingOlder(false);
    prevMsgCountRef.current = 0;
    forceScrollRef.current = true;
    anchorScrollHeightRef.current = null;
  }, [partyCode]);

  // ── Combine the tail of confirmed messages with optimistic ──────────────
  //
  // Only the newest `visibleCount` confirmed messages are rendered.
  // Optimistic always renders below the tail — hiding in-flight sends
  // would feel like a bug.
  const combined = useMemo(() => {
    const visibleConfirmed =
      visibleCount >= messages.length
        ? messages
        : messages.slice(-visibleCount);
    if (optimisticMessages.size === 0) return visibleConfirmed;
    return [...visibleConfirmed, ...Array.from(optimisticMessages.values())];
  }, [messages, visibleCount, optimisticMessages]);

  const canLoadOlder = visibleCount < messages.length;

  // ── Auto-scroll behavior ────────────────────────────────────────────────
  useEffect(() => {
    const node = scrollRef.current;
    const anchor = anchorRef.current;
    if (!node || !anchor) return;

    const prev = prevMsgCountRef.current;
    const next = combined.length;
    prevMsgCountRef.current = next;

    // No-op on first paint of an empty room.
    if (next === 0) return;

    const grew = next > prev;
    const force = forceScrollRef.current;
    forceScrollRef.current = false;

    if (force) {
      anchor.scrollIntoView({ block: 'end' });
      setUnreadCount(0);
      return;
    }

    if (!grew) return;

    if (scrolledAwayRef.current) {
      // User is reading older messages — don't yank the scroll. Bump the
      // pill counter so they can opt-in to jump.
      setUnreadCount((c) => c + (next - prev));
    } else {
      anchor.scrollIntoView({ block: 'end' });
    }
  }, [combined]);

  // ── Scroll handler — bottom (scrolled-away pill) + top (load-older) ─────
  const handleScroll = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;

    // Bottom: drive the scrolled-away flag for the NewMessagesPill.
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
    const away = distance > NEAR_BOTTOM_PX;
    setScrolledAway(away);
    if (!away) setUnreadCount(0);

    // Top: reveal more of the in-memory list. The guards inside loadOlder
    // make repeated fires cheap.
    if (node.scrollTop < NEAR_TOP_PX && canLoadOlder) {
      loadOlder();
    }
  }, [loadOlder, canLoadOlder]);

  // ── Send handler ────────────────────────────────────────────────────────
  const handleSend = useCallback(
    async (text) => {
      if (!text) return;
      const senderId = user || handle || 'anon';
      const senderName = handle || 'anon';

      // Push optimistic immediately.
      const localId = `local-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;
      const optimistic = {
        id: localId,
        text,
        senderId,
        senderName,
        avatarSeed: avatarSeed || senderId,
        sentAt: null, // "just now" until server resolves
        replyTo: null,
        reactions: {},
        status: 'pending',
      };
      setOptimisticMessages((prev) => {
        const next = new Map(prev);
        next.set(localId, optimistic);
        return next;
      });
      // Own send → force scroll to bottom regardless of scrolledAway.
      forceScrollRef.current = true;
      setScrolledAway(false);

      // Stamp a per-message fail timer. If the snapshot listener hasn't
      // promoted this localId to a confirmed doc within OPTIMISTIC_TIMEOUT_MS,
      // flip it to 'failed'. The dedupe-on-confirm path clears the timer.
      const timer = setTimeout(() => {
        failTimersRef.current.delete(localId);
        setOptimisticMessages((prev) => {
          const cur = prev.get(localId);
          if (!cur || cur.status === 'failed') return prev;
          const next = new Map(prev);
          next.set(localId, { ...cur, status: 'failed' });
          return next;
        });
      }, OPTIMISTIC_TIMEOUT_MS);
      failTimersRef.current.set(localId, timer);

      try {
        await sendMessage({
          code: partyCode,
          text,
          senderId,
          senderName,
          avatarSeed,
        });
        // The snapshot listener will dedupe by sender + text. If a network
        // race leaves the optimistic in place, the user just sees their own
        // message twice for a tick — preferable to a missing-message bug.
        //
        // Sending is the action that makes a room "qualify" for the room
        // history (the user has now interacted in it). Record it optimistically
        // so it appears in the sidebar / switch-room sheet immediately, without
        // waiting for the next per-tab-session refetch.
        recordRoomActivity(user, partyCode, Date.now());
      } catch (err) {
        // Mark failed in place so the bubble shows a retry affordance.
        const timerToClear = failTimersRef.current.get(localId);
        if (timerToClear) {
          clearTimeout(timerToClear);
          failTimersRef.current.delete(localId);
        }
        setOptimisticMessages((prev) => {
          const next = new Map(prev);
          const cur = next.get(localId);
          if (cur) {
            next.set(localId, { ...cur, status: 'failed' });
          }
          return next;
        });
      }
    },
    [user, handle, partyCode, avatarSeed],
  );

  // ── Jump-to-bottom from the pill ────────────────────────────────────────
  const handleJumpToLatest = useCallback(() => {
    forceScrollRef.current = true;
    setUnreadCount(0);
    setScrolledAway(false);
    if (anchorRef.current) {
      anchorRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, []);

  const showEmpty =
    partyCode && partyCode !== GLOB && messages.length === 0;

  return (
    <section
      className="glacier-chat-region"
      data-testid="chat-region"
      aria-label="Chat"
    >
      <ExpiredPartyBanner />
      <BannerHost />
      <DisconnectedBanner />
      <div
        ref={scrollRef}
        className={
          'glacier-chat-region__scroll' +
          (showEmpty ? ' glacier-chat-region__scroll--center' : '')
        }
        onScroll={handleScroll}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
      >
        {showEmpty ? (
          <EmptyParty code={partyCode} />
        ) : (
          <>
            {loadingOlder && (
              <div
                className="glacier-chat-region__loading-older"
                data-testid="loading-older"
                aria-live="polite"
              >
                Loading older messages…
              </div>
            )}
            <MessageList
              ref={anchorRef}
              messages={combined}
              selfId={selfId}
            />
            <TypingIndicator users={typers} />
          </>
        )}
      </div>
      <NewMessagesPill count={unreadCount} onClick={handleJumpToLatest} />
      <Composer onSend={handleSend} disabled={!online} />
    </section>
  );
}

export default ChatRegion;
